import React, { useCallback, useEffect, useState } from "react";
import { Row, Form, Button } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.css";
import "./window.css";
import { CSVLink } from "react-csv";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import * as XLSX from "xlsx";
import logo from "../images/varstack2_logo.jpg";

const api_key = process.env.REACT_APP_API_KEY;

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const cancer_types = require("../cancer-types.json");

/**
 * Maps database names to their CSV-ready row arrays.
 * @type {Map<string, Array<Object>>}
 */
let databases = new Map();

/**
 * Maps database names to their CSV-ready row arrays.
 * @type {Map<string, Array<Object>>}
 */
let csvMap = new Map();

/**
 * Maps database names to their CSV header definitions.
 * @type {Map<string, Array<{label: string, key: string}>>}
 */
let headersMap = new Map();

/**
 * Registers a new database entry with its fetch function and initializes
 * empty CSV data and header stores for it.
 *
 * @param {string} c - The display name of the database
 * @param {Function} f - The async function used to fetch data from this database.
 */
function newDatabase(c, f) {
  databases.set(c, f);
  csvMap.set(c, []);
  headersMap.set(c, []);
}

newDatabase("ClinVar", getClinVar);
newDatabase("cBioPortal", getcBioPortal);
newDatabase("UCSC Genome Browser", getUCSC);
newDatabase("ClinicalTrials.gov", getClinicalTrials);
newDatabase("CIViC", getCIViC);

/**
 * Placeholder list of peptide changes shown before a gene query is submitted. Is then updated with user queried peptides.
 * @type {string[]}
 */
let peptides = ["Submit Gene ID First"];

/**
 * Placeholder list of coding sequence changes shown before a gene query is submitted. Is then updated with user queried
 * @type {string[]}
 */
let codons = ["Submit Gene ID First"];

/**
 * Fetches mutation data from the COSMIC database via the NLM Clinical Tables API.
 * Builds an HTML table of results and populates {@link csvMap} and {@link headersMap}
 * with the "COSMIC" key for CSV export.
 *
 * Currently not in use due to licensing issues.
 *
 * @async
 * @param {string} gene - Hugo gene symbol (e.g. "KRAS"). Required.
 * @param {string} peptide - Amino acid change in HGVS protein notation (e.g. "p.G12V"). Optional.
 * @param {string} codon - Coding sequence change (e.g. "c.35G>T"). Optional.
 * @returns {Promise<string>} HTML string containing a `<table>` of COSMIC mutations,
 *   or an error message string if `gene` is empty.
 */
async function getCosmic(gene, peptide, codon) {
  if (gene === "") {
    return "Please enter a gene name";
  }
  let link = "";
  if (peptide === "" && codon === "") {
    link = `https://clinicaltables.nlm.nih.gov/api/cosmic/v4/search?terms=${gene}&maxList=&grchv=38&df=AccessionNumber,GeneCDS_Length,GeneName,HGNC_ID,MutationAA,MutationCDS,MutationDescription,MutationGenomePosition,MutationStrand,MutationID,LegacyMutationID,GenomicMutationID,Name,PrimaryHistology,PrimarySite,PubmedPMID,Site,GRChVer`;
  } else if (peptide === "") {
    link = `https://clinicaltables.nlm.nih.gov/api/cosmic/v4/search?terms=${gene},${codon}&maxList=&grchv=38&df=AccessionNumber,GeneCDS_Length,GeneName,HGNC_ID,MutationAA,MutationCDS,MutationDescription,MutationGenomePosition,MutationStrand,MutationID,LegacyMutationID,GenomicMutationID,Name,PrimaryHistology,PrimarySite,PubmedPMID,Site,GRChVer`;
  } else if (codon === "") {
    link = `https://clinicaltables.nlm.nih.gov/api/cosmic/v4/search?terms=${gene},"${peptide}"&maxList=&grchv=38&df=AccessionNumber,GeneCDS_Length,GeneName,HGNC_ID,MutationAA,MutationCDS,MutationDescription,MutationGenomePosition,MutationStrand,MutationID,LegacyMutationID,GenomicMutationID,Name,PrimaryHistology,PrimarySite,PubmedPMID,Site,GRChVer`;
  } else {
    link = `https://clinicaltables.nlm.nih.gov/api/cosmic/v4/search?terms=${gene},"${peptide}",${codon}&maxList=&grchv=38&df=AccessionNumber,GeneCDS_Length,GeneName,HGNC_ID,MutationAA,MutationCDS,MutationDescription,MutationGenomePosition,MutationStrand,MutationID,LegacyMutationID,GenomicMutationID,Name,PrimaryHistology,PrimarySite,PubmedPMID,Site,GRChVer`;
  }

  let json = await fetch(link)
    .then((response) => response.json())
    .then((responseObject) => {
      let html = `<table> <tr> <th> Accession Number </th> <th> Gene CDS Length</th> <th> Gene Name </th> <th> HGNC ID </th> <th> Peptide Change </th> <th> Coding Sequence Change </th> <th> Mutation Description </th> <th> Mutation Genome Position </th> <th>Mutation Strand</th> <th>Mutation ID</th> <th>Legacy Mutation ID</th> <th>Genomic Mutation ID</th> <th>Primary Histology</th> <th>Primary Site</th> </tr>`;
      let temp = responseObject[3];
      let allCosmicData = [];
      let cosmicData = {};
      let cosmicHeaders = [
        "Accession Number",
        "Gene CDS Length",
        "Gene Name",
        "HGNC ID",
        "Peptide Change",
        "Coding Sequence Change",
        "Mutation Description",
        "Mutation Genome Position",
        "Mutation Strand",
        "Mutation ID",
        "Legacy Mutation ID",
        "Genomic Mutation ID",
        "placeholder",
        "Primary Histology",
        "Primary Site",
      ];
      for (let j = 0; j < temp.length; j++) {
        html += `<tr>`;
        for (let i = 0; i < 15; i++) {
          if (i !== 12) {
            html += `<td>${temp[j][i]}</td>`;
            cosmicData[cosmicHeaders[i]] = temp[j][i];
          }
        }
        allCosmicData.push(cosmicData);
        cosmicData = {};
        html += `</tr>`;
      }
      html += `</table>`;
      csvMap.set("Cosmic", allCosmicData);
      let cosmicHeaderObj = [];
      cosmicHeaders.forEach((ele) => {
        cosmicHeaderObj.push({ label: ele, key: ele });
      });
      headersMap.set("Cosmic", cosmicHeaderObj);
      return html;
    });

  return json;
}

/**
 * Constructs a ClinVar search URL for the given gene/peptide/codon combination
 * and returns it embedded in an `<iframe>`. Also stores the link in {@link csvMap}
 * under "ClinVar" for CSV export.
 *
 * @async
 * @param {string} gene - Hugo gene symbol. Required.
 * @param {string} peptide - Amino acid change (e.g. "p.G12V"). Optional.
 * @param {string} codon - Coding sequence change (e.g. "c.35G>T"). Optional.
 *   The `>` character is HTML-escaped to `&gt;` to prevent markup injection.
 * @returns {Promise<string>} An `<iframe>` HTML string pointing to the NCBI ClinVar
 *   search results page, or an error message if `gene` is empty.
 */
async function getClinVar(gene, peptide, codon) {
  if (gene === "") {
    return "Please enter a gene name";
  }
  let codonEscapedStr = codon.replace(">", "&gt;"); // such that when ">" is included in codon, it is used as text and not HTML
  let link = `https://www.ncbi.nlm.nih.gov/clinvar/?term="${gene}"[GENE]+"${peptide}"[VARNAME]+"${codonEscapedStr}"[VARNAME]`;

  csvMap.set("ClinVar", [{ link: link }]);
  headersMap.set("ClinVar", [{ label: "link", key: "link" }]);
  return `<iframe src=${link}></iframe>`;
}

/**
 * Chart.js options for the cBioPortal horizontal bar chart.
 *
 * @type {import('chart.js').ChartOptions<'bar'>}
 */
export let chartOptions = {
  indexAxis: "y",
  elements: {
    bar: {
      borderWidth: 2,
    },
  },
  responsive: true,
  plugins: {
    legend: {
      position: "right",
    },
    title: {
      display: true,
      text: "",
      font: {
        size: 24,
      },
    },
  },
};

/**
 * Mutable Chart.js dataset object shared between {@link getcBioPortal} and the
 * rendered `<Bar>` component. Labels and data arrays are replaced on each fetch.
 *
 * @type {import('chart.js').ChartData<'bar'>}
 */
export let chartData = {
  labels: [],
  datasets: [
    {
      label: "",
      data: [],
      borderColor: "rgb(53, 162, 235)",
      backgroundColor: "rgba(53, 162, 235, 0.5)",
    },
  ],
};

/**
 * Fetches mutation frequency data from cBioPortal for a specific gene and
 * peptide change across all studies matching the given cancer type keyword.
 * Mutation counts are normalised to the study with the highest count (0–100 scale).
 * Populates {@link chartData} and {@link chartOptions} in place, then returns
 * a rendered `<Bar>` React element.
 *
 * @async
 * @param {string} gene - Hugo gene symbol (e.g. "KRAS"). Required.
 * @param {string} peptide - Amino acid change, with or without the "p." prefix
 *   (e.g. "p.G12V" or "G12V"). Comparison is case-insensitive. Required.
 * @param {string} codon - Coding sequence change. Not used by this function
 *   but kept for a consistent signature across all database fetch functions.
 * @param {string} cancer - Cancer type keyword used to filter cBioPortal studies
 *   (e.g. "lung"). Required.
 * @returns {Promise<React.ReactElement|string>} A Chart.js `<Bar>` element showing
 *   normalised mutation frequency per study, or an error string if `gene` is empty.
 */
async function getcBioPortal(gene, peptide, codon, cancer) {
  console.log("bioportal", gene, peptide, codon, cancer);
  if (gene === "") {
    return "Please enter a gene name test";
  }
  if (peptide.slice(0, 2) === "p.") {
    peptide = peptide.slice(2);
  }
  peptide = peptide.toLowerCase();
  // Step 1: Retrieve all study IDs matching the cancer keyword
  let studyIDs = await fetch(
    `https://www.cbioportal.org/api/studies?direction=ASC&keyword=${cancer}&pageNumber=0&pageSize=10000000&projection=SUMMARY&sortBy=cancerTypeId`,
  )
    .then((response) => response.json())
    .then((responseObject) => {
      let studies = [];
      responseObject.forEach((ele) => {
        studies.push(ele.studyId);
      });
      return studies;
    });
  // Step 2: Look up the Entrez gene ID for the supplied symbol
  let geneinfo = await fetch(`https://www.cbioportal.org/api/genes/${gene}`)
    .then((response) => response.json())
    .then((responseObject) => {
      return responseObject.entrezGeneId;
    });

  // Step 3: For each study, count how many mutations match the target peptide change
  let mutationCount = new Map();
  for (let i = 0; i < studyIDs.length; i++) {
    let link = `https://www.cbioportal.org/api/molecular-profiles/${
      studyIDs[i] + "_mutations"
    }/mutations?direction=ASC&entrezGeneId=${geneinfo}&pageNumber=0&pageSize=10000000&projection=SUMMARY&sampleListId=${
      studyIDs[0] + "_all"
    }`;
    let mutations = await fetch(
      `https://www.cbioportal.org/api/molecular-profiles/${
        studyIDs[i] + "_mutations"
      }/mutations?direction=ASC&entrezGeneId=${geneinfo}&pageNumber=0&pageSize=10000000&projection=SUMMARY&sampleListId=${
        studyIDs[i] + "_all"
      }`,
    )
      .then((response) => response.json())
      .then((responseObject) => {
        let count = 0;
        responseObject &&
          responseObject.forEach((ele) => {
            if (ele.proteinChange.toLowerCase() === peptide) {
              count++;
            }
          });
        return count;
      });
    mutationCount.set(studyIDs[i], mutations);
  }

  // Step 4: Normalise counts relative to the study with the highest count
  let chartLabels = [];
  chartData.datasets[0].data = [];
  let cBioData = {};
  let cBioHeaders = [];
  let html = `<div class="chart-wrap">
    <h2 class="title">Frequency of ${peptide} in ${gene}</h2>
    <div class="grid horizontal"> `;
  chartOptions.plugins.title.text = `Frequency of p.${peptide.toUpperCase()} in ${gene} `;
  let max = 0;
  mutationCount.forEach((value, key, map) => {
    if (value > max) {
      max = value;
    }
  });
  mutationCount.forEach((value, key, map) => {
    html += `<div class="bar" style="--bar-value:${
      (value / max) * 100
    }%;" data-name=${key} title=${value}></div>`;
    cBioHeaders.push(key);
    cBioData[key] = value;
    chartLabels.push(key);
    chartData.datasets[0].data.push((value / max) * 100);
    chartData.datasets[0].label = `p.${peptide.toUpperCase()}`;
  });
  html += `</div>
    </div>`;
  csvMap.set("cBioPortal", [cBioData]);
  console.log("bioportal data", csvMap);
  let cBioHeaderObj = [];
  cBioHeaders.forEach((ele) => {
    cBioHeaderObj.push({ label: ele, key: ele });
  });
  headersMap.set("cBioPortal", cBioHeaderObj);

  chartData.labels = chartLabels;
  return <Bar options={chartOptions} data={chartData} />;
}

/**
 * Fetches the genomic position for the given variant from COSMIC, then
 * embeds the UCSC Genome Browser (GRCh38) centred on that position in an
 * `<iframe>`. Also stores the generated URL in {@link csvMap} under
 * "UCSC Genome Browser" for CSV export.
 *
 * @async
 * @param {string} gene - Hugo gene symbol. Required.
 * @param {string} peptide - Amino acid change. Optional.
 * @param {string} codon - Coding sequence change. Optional.
 * @returns {Promise<string>} An `<iframe>` HTML string pointing to the UCSC
 *   Genome Browser, or an error message if `gene` is empty.
 */
async function getUCSC(gene, peptide, codon) {
  if (gene === "") {
    return "Please enter a gene name";
  }

  // Build COSMIC query to retrieve the genome position
  let link = "";
  if (peptide === "" && codon === "") {
    link = `https://clinicaltables.nlm.nih.gov/api/cosmic/v4/search?terms=${gene}&maxList=&grchv=38&df=AccessionNumber,GeneCDS_Length,GeneName,HGNC_ID,MutationAA,MutationCDS,MutationDescription,MutationGenomePosition,MutationStrand,MutationID,LegacyMutationID,GenomicMutationID,Name,PrimaryHistology,PrimarySite,PubmedPMID,Site,GRChVer`;
  } else if (peptide === "") {
    link = `https://clinicaltables.nlm.nih.gov/api/cosmic/v4/search?terms=${gene},${codon}&maxList=&grchv=38&df=AccessionNumber,GeneCDS_Length,GeneName,HGNC_ID,MutationAA,MutationCDS,MutationDescription,MutationGenomePosition,MutationStrand,MutationID,LegacyMutationID,GenomicMutationID,Name,PrimaryHistology,PrimarySite,PubmedPMID,Site,GRChVer`;
  } else if (codon === "") {
    link = `https://clinicaltables.nlm.nih.gov/api/cosmic/v4/search?terms=${gene},${peptide}&maxList=&grchv=38&df=AccessionNumber,GeneCDS_Length,GeneName,HGNC_ID,MutationAA,MutationCDS,MutationDescription,MutationGenomePosition,MutationStrand,MutationID,LegacyMutationID,GenomicMutationID,Name,PrimaryHistology,PrimarySite,PubmedPMID,Site,GRChVer`;
  } else {
    link = `https://clinicaltables.nlm.nih.gov/api/cosmic/v4/search?terms=${gene},${peptide},${codon}&maxList=&grchv=38&df=AccessionNumber,GeneCDS_Length,GeneName,HGNC_ID,MutationAA,MutationCDS,MutationDescription,MutationGenomePosition,MutationStrand,MutationID,LegacyMutationID,GenomicMutationID,Name,PrimaryHistology,PrimarySite,PubmedPMID,Site,GRChVer`;
  }
  let position = await fetch(link)
    .then((response) => response.json())
    .then((responseObject) => {
      let temp = responseObject[3];
      return temp[0][7];
    });
  let linki = `https://genome-euro.ucsc.edu/cgi-bin/hgTracks?db=hg38&lastVirtModeType=default&lastVirtModeExtraState=&virtModeType=default&virtMode=0&nonVirtPosition=&position=chr${position}&hgsid=1610342553_KAlVD6SwtlZYQeDcjFhLmAd0uEV2&apiKey=MO44gu3mkE2KBvDAVLoaxaZAhcUeclew2XSL0DxhNCcZ`;
  csvMap.set("UCSC Genome Browser", [{ link: linki }]);
  headersMap.set("UCSC Genome Browser", [{ label: "link", key: "link" }]);

  return `<iframe className = "UCSC-browser" src=${linki}> </iframe>`;
}

/**
 * Fetches clinical trials from ClinicalTrials.gov that match the given gene
 * and peptide change. Builds an HTML table with trial status, title, conditions,
 * interventions, and up to 3 locations per study. Populates {@link csvMap} and
 * {@link headersMap} under "ClinicalTrials.gov" for CSV export.
 *
 * @async
 * @param {string} gene - Hugo gene symbol. Required.
 * @param {string} peptide - Amino acid change; the "p." prefix is stripped before
 *   the API call if present. Optional.
 * @param {string} codon - Coding sequence change. Not used in the API query but
 *   kept for a consistent signature.
 * @returns {Promise<string>} HTML string containing a `<table>` of clinical trial
 *   results, or an error message if `gene` is empty.
 */
async function getClinicalTrials(gene, peptide, codon) {
  if (gene === "") {
    return "Please enter a gene name";
  }
  if (peptide.slice(0, 2) === "p.") {
    peptide = peptide.slice(2);
  }
  let link = "";
  link = `https://clinicaltrials.gov/api/v2/studies?format=json&query.term=${gene}+${peptide}`;
  let json = await fetch(link)
    .then((response) => response.json())
    .then((responseObject) => {
      let allTrialData = [];
      let trialData = {};
      let trialHeadings = [
        "Link",
        "Status",
        "Study Title",
        "Conditions",
        "Intervention",
        "Locations",
      ];
      let html = `<table> <tr> <th> Link </th> <th> Status </th> <th> Study Title</th> <th> Conditions </th> <th> Interventions </th> <th> Locations </th></tr>`;
      let studies = responseObject.studies;
      for (let i = 0; i < studies.length; i++) {
        let s = studies[i].protocolSection;
        html += `<tr>`;
        html += `<td><a href= "https://clinicaltrials.gov/study/${s.identificationModule.nctId}" target="_blank">${s.identificationModule.nctId}</td>`; // NCT ID
        html += `<td>${s.statusModule.overallStatus}</td>`; // Status
        trialData["Status"] = s.statusModule.overallStatus;
        html += `<td>${s.identificationModule.briefTitle}</td>`; // Study Title
        trialData["Study Title"] = s.identificationModule.briefTitle;
        html += `<td><ul>`; // Conditions
        let conditiondata = "";
        for (let j = 0; j < s.conditionsModule.conditions.length; j++) {
          html += `<li>${s.conditionsModule.conditions[j]}</li>`;
          conditiondata += s.conditionsModule.conditions[j] + "\n";
        }
        html += `</ul></td>`;
        trialData["Conditions"] = conditiondata;
        html += `<td><ul>`; // Interventions
        let interventions = "";
        if (s.armsInterventionsModule) {
          if (s.armsInterventionsModule.interventions) {
            for (
              let j = 0;
              j < s.armsInterventionsModule.interventions.length;
              j++
            ) {
              html += `<li>${s.armsInterventionsModule.interventions[j].type}: ${s.armsInterventionsModule.interventions[j].name}</li>`;
              interventions +=
                s.armsInterventionsModule.interventions[j].type +
                ": " +
                s.armsInterventionsModule.interventions[j].name +
                "\n";
            }
          }
        } else {
          // if intervention does not exist
          interventions = html += `<li>Not Available</li>`;
        }
        html += `</ul></td>`;
        trialData["Interventions"] = interventions;
        html += `<td><ul>`; //Locations
        let locationdata = "";
        if (s.contactsLocationsModule) {
          if (!s.contactsLocationsModule.hasOwnProperty("locations")) {
            html += `No Location Data`;
            locationdata = `No Location Data`;
          } else {
            if (s.contactsLocationsModule.locations.length > 3) {
              for (let j = 0; j < 3; j++) {
                if (
                  !s.contactsLocationsModule.locations[j].hasOwnProperty(
                    "state",
                  )
                ) {
                  html += `<li>${s.contactsLocationsModule.locations[j].facility}, ${s.contactsLocationsModule.locations[j].city}, ${s.contactsLocationsModule.locations[j].country} </li>`;
                  locationdata +=
                    s.contactsLocationsModule.locations[j].facility +
                    ", " +
                    s.contactsLocationsModule.locations[j].city +
                    ", " +
                    s.contactsLocationsModule.locations[j].country +
                    "\n";
                } else {
                  html += `<li>${s.contactsLocationsModule.locations[j].facility}, ${s.contactsLocationsModule.locations[j].city}, ${s.contactsLocationsModule.locations[j].state}, ${s.contactsLocationsModule.locations[j].country} </li>`;
                  locationdata +=
                    s.contactsLocationsModule.locations[j].facility +
                    ", " +
                    s.contactsLocationsModule.locations[j].city +
                    ", " +
                    s.contactsLocationsModule.locations[j].state +
                    ", " +
                    s.contactsLocationsModule.locations[j].country +
                    "\n";
                }
              }
              html += `<li>(and ${
                s.contactsLocationsModule.locations.length - 3
              } more ...)</li>`;
              locationdata += `(and ${
                s.contactsLocationsModule.locations.length - 3
              } more ...)`;
            } else {
              for (
                let j = 0;
                j < s.contactsLocationsModule.locations.length;
                j++
              ) {
                if (
                  !s.contactsLocationsModule.locations[j].hasOwnProperty(
                    "state",
                  )
                ) {
                  html += `<li>${s.contactsLocationsModule.locations[j].facility}, ${s.contactsLocationsModule.locations[j].city}, ${s.contactsLocationsModule.locations[j].country} </li>`;
                  locationdata += `${s.contactsLocationsModule.locations[j].facility}, ${s.contactsLocationsModule.locations[j].city}, ${s.contactsLocationsModule.locations[j].country} \n`;
                } else {
                  html += `<li>${s.contactsLocationsModule.locations[j].facility}, ${s.contactsLocationsModule.locations[j].city}, ${s.contactsLocationsModule.locations[j].state}, ${s.contactsLocationsModule.locations[j].country} </li>`;
                  locationdata += `${s.contactsLocationsModule.locations[j].facility}, ${s.contactsLocationsModule.locations[j].city}, ${s.contactsLocationsModule.locations[j].state}, ${s.contactsLocationsModule.locations[j].country} \n`;
                }
              }
            }
          }
        } else {
          html += `<li>Not Applicable</li>`;
        }
        html += `</ul></td>`;
        trialData["Locations"] = locationdata;
        html += `</tr>`;
        allTrialData.push(trialData);
        trialData = {};
      }
      html += `</table>`;
      csvMap.set("ClinicalTrials.gov", allTrialData);
      let trialHeaderObj = [];
      trialHeadings.forEach((ele) => {
        trialHeaderObj.push({ label: ele, key: ele });
      });
      headersMap.set("ClinicalTrials.gov", trialHeaderObj);
      return html;
    });
  return json;
}

/**
 * Queries the CIViC GraphQL API for variants of the given gene, optionally
 * filtered by variant name. For each matching variant, retrieves molecular
 * profiles and their associated evidence items (disease, therapies, evidence
 * level/type/direction, sources). Builds an HTML table and populates
 * {@link csvMap} and {@link headersMap} under "CIViC" for CSV export.
 *
 * @async
 * @param {string} gene - Hugo gene symbol (e.g. "BRAF"). Required.
 * @param {string} peptide - Amino acid change; the "p." prefix is stripped if
 *   present (e.g. "p.V600E" → "V600E"). Used as the GraphQL `variantName`
 *   filter. Pass an empty string to retrieve all variants.
 * @param {string} codon - Coding sequence change. Not used by this function
 *   but kept for a consistent signature.
 * @returns {Promise<string>} HTML string containing a `<table>` of CIViC
 *   evidence items, or an error/empty-state message string on failure.
 */
async function getCIViC(gene, peptide, codon) {
  if (gene === "") {
    return "Please enter a gene name";
  }

  // Clean peptide format if needed
  let cleanPeptide = peptide;
  if (peptide.slice(0, 2) === "p.") {
    cleanPeptide = peptide.slice(2);
  }

  // If no peptide provided, leave variantName empty to get all variants
  let variantName = cleanPeptide;

  try {
    // GraphQL query
    const query = `
      query GeneVariants($geneName: String!, $variantName: String) {
        gene(entrezSymbol: $geneName) {
          variants(name: $variantName) {
            pageInfo {
              hasNextPage
              endCursor
            }
            nodes {
              name
              id
              link
              ... on GeneVariant {
                alleleRegistryId
                coordinates {
                  start
                  stop
                  chromosome
                  representativeTranscript
                }
              }
              molecularProfiles {
                pageInfo {
                  hasNextPage
                  endCursor
                }
                nodes {
                  id
                  name
                  evidenceItems {
                    pageInfo {
                      hasNextPage
                      endCursor
                    }
                    nodes {
                      id
                      status
                      molecularProfile {
                        id
                        name
                        link
                      }
                      evidenceType
                      evidenceLevel
                      evidenceRating
                      evidenceDirection
                      phenotypes {
                        id
                        hpoId
                        name
                      }
                      description
                      disease {
                        id
                        doid
                        name
                        diseaseAliases
                        displayName
                      }
                      therapies {
                        id
                        ncitId
                        name
                        therapyAliases
                      }
                      source {
                        ascoAbstractId
                        citationId
                        pmcId
                        sourceType
                        title
                      }
                      therapyInteractionType
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const variables = {
      geneName: gene,
      variantName: variantName || null,
    };

    let json = await fetch("https://civicdb.org/api/graphql", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: query,
        variables: variables,
      }),
    })
      .then((response) => response.json())
      .then((responseObject) => {
        console.log("Civic", responseObject);
        let html = `<table> <tr> 
          <th>Variant Name</th>
          <th>Variant ID</th>
          <th>Coordinates</th>
          <th>Allele Registry ID</th>
          <th>Molecular Profile</th>
          <th>Disease</th>
          <th>Evidence Type</th>
          <th>Evidence Level</th>
          <th>Evidence Rating</th>
          <th>Evidence Direction</th>
          <th>Therapies</th>
          <th>Therapy Interaction</th>
          <th>Description</th>
          <th>Source</th>
          <th>CIViC Link</th>
        </tr>`;

        let allCivicData = [];
        let civicHeaders = [
          "Variant Name",
          "Variant ID",
          "Coordinates",
          "Allele Registry ID",
          "Molecular Profile",
          "Disease",
          "Evidence Type",
          "Evidence Level",
          "Evidence Rating",
          "Evidence Direction",
          "Therapies",
          "Therapy Interaction",
          "Description",
          "Source",
          "CIViC Link",
        ];

        if (
          responseObject.data &&
          responseObject.data.gene &&
          responseObject.data.gene.variants &&
          responseObject.data.gene.variants.nodes.length > 0
        ) {
          responseObject.data.gene.variants.nodes.forEach((variant) => {
            // Get molecular profiles
            console.log("variant", variant);
            const molecularProfiles = variant.molecularProfiles?.nodes || [];

            // Prepare coordinate string
            let coordinateStr = "N/A";
            if (variant.coordinates) {
              coordinateStr = `chr${variant.coordinates.chromosome}:${variant.coordinates.start}-${variant.coordinates.stop} (${variant.coordinates.representativeTranscript})`;
            }

            let hasEvidence = false;

            molecularProfiles.forEach((profile) => {
              const evidenceItems = profile.evidenceItems?.nodes || [];

              if (evidenceItems.length > 0) {
                evidenceItems.forEach((evidence) => {
                  hasEvidence = true;
                  let civicData = {};

                  html += `<tr>`;

                  // Variant Name
                  html += `<td>${variant.name || "N/A"}</td>`;
                  civicData["Variant Name"] = variant.name || "N/A";

                  // Variant ID
                  html += `<td>${variant.id || "N/A"}</td>`;
                  civicData["Variant ID"] = variant.id || "N/A";

                  // Coordinates
                  html += `<td>${coordinateStr}</td>`;
                  civicData["Coordinates"] = coordinateStr;

                  // Allele Registry ID
                  html += `<td>${variant.alleleRegistryId || "N/A"}</td>`;
                  civicData["Allele Registry ID"] =
                    variant.alleleRegistryId || "N/A";

                  // Molecular Profile
                  html += `<td>${profile.name || "N/A"}</td>`;
                  civicData["Molecular Profile"] = profile.name || "N/A";

                  // Disease
                  const disease =
                    evidence.disease?.displayName ||
                    evidence.disease?.name ||
                    "N/A";
                  html += `<td>${disease}</td>`;
                  civicData["Disease"] = disease;

                  // Evidence Type
                  html += `<td>${evidence.evidenceType || "N/A"}</td>`;
                  civicData["Evidence Type"] = evidence.evidenceType || "N/A";

                  // Evidence Level
                  html += `<td>${evidence.evidenceLevel || "N/A"}</td>`;
                  civicData["Evidence Level"] = evidence.evidenceLevel || "N/A";

                  // Evidence Rating
                  html += `<td>${evidence.evidenceRating || "N/A"}</td>`;
                  civicData["Evidence Rating"] =
                    evidence.evidenceRating || "N/A";

                  // Evidence Direction
                  html += `<td>${evidence.evidenceDirection || "N/A"}</td>`;
                  civicData["Evidence Direction"] =
                    evidence.evidenceDirection || "N/A";

                  // Therapies
                  const therapies =
                    evidence.therapies?.map((t) => t.name).join(", ") || "N/A";
                  html += `<td>${therapies}</td>`;
                  civicData["Therapies"] = therapies;

                  // Therapy Interaction Type
                  html += `<td>${
                    evidence.therapyInteractionType || "N/A"
                  }</td>`;
                  civicData["Therapy Interaction"] =
                    evidence.therapyInteractionType || "N/A";

                  // Description (truncated for display)
                  const description = evidence.description || "N/A";
                  const truncatedDescription =
                    description.length > 200
                      ? description.substring(0, 200) + "..."
                      : description;
                  html += `<td>${truncatedDescription}</td>`;
                  civicData["Description"] = description;

                  // Source
                  let source = "N/A";
                  if (evidence.source) {
                    const sourceId =
                      evidence.source.pmcId ||
                      evidence.source.citationId ||
                      evidence.source.ascoAbstractId ||
                      "";
                    source = `${evidence.source.sourceType}: ${sourceId}${
                      evidence.source.title ? " - " + evidence.source.title : ""
                    }`;
                  }
                  html += `<td>${source}</td>`;
                  civicData["Source"] = source;

                  // CIViC Link
                  const civicLink = variant.link || "N/A";
                  console.log("civic link", civicLink);
                  html += `<td><a href="https://civicdb.org/${civicLink}" target="_blank">View on CIViC</a></td>`;
                  civicData["CIViC Link"] = civicLink;

                  html += `</tr>`;

                  allCivicData.push(civicData);
                });
              }
            });

            // If no evidence items found, still show the variant
            if (!hasEvidence) {
              let civicData = {};

              html += `<tr>`;

              html += `<td>${variant.name || "N/A"}</td>`;
              civicData["Variant Name"] = variant.name || "N/A";

              html += `<td>${variant.id || "N/A"}</td>`;
              civicData["Variant ID"] = variant.id || "N/A";

              html += `<td>${coordinateStr}</td>`;
              civicData["Coordinates"] = coordinateStr;

              html += `<td>${variant.alleleRegistryId || "N/A"}</td>`;
              civicData["Allele Registry ID"] =
                variant.alleleRegistryId || "N/A";

              html += `<td colspan="10">No evidence items available</td>`;
              civicData["Molecular Profile"] = "N/A";
              civicData["Disease"] = "N/A";
              civicData["Evidence Type"] = "N/A";
              civicData["Evidence Level"] = "N/A";
              civicData["Evidence Rating"] = "N/A";
              civicData["Evidence Direction"] = "N/A";
              civicData["Therapies"] = "N/A";
              civicData["Therapy Interaction"] = "N/A";
              civicData["Description"] = "No evidence items available";
              civicData["Source"] = "N/A";

              const civicLink = variant.link || "N/A";
              html += `<td><a href="${civicLink}" target="_blank">View on CIViC</a></td>`;
              civicData["CIViC Link"] = civicLink;

              html += `</tr>`;

              allCivicData.push(civicData);
            }
          });
        } else {
          html += `<tr><td colspan="15">No variants found for ${gene}${
            variantName ? " with variant " + variantName : ""
          }</td></tr>`;
        }

        html += `</table>`;

        csvMap.set("CIViC", allCivicData);
        let civicHeaderObj = [];
        civicHeaders.forEach((ele) => {
          civicHeaderObj.push({ label: ele, key: ele });
        });
        headersMap.set("CIViC", civicHeaderObj);

        return html;
      });

    return json;
  } catch (error) {
    console.error("CIViC GraphQL API Error:", error);
    return `<div>Error fetching CIViC data: ${error.message}</div>`;
  }
}

/**
 * @typedef {Object} DataContainerProps
 * @property {string} dataset - Key into {@link databases}, {@link csvMap}, and
 *   {@link headersMap} (e.g.  "ClinVar").
 * @property {string} gene   - Hugo gene symbol passed to the fetch function.
 * @property {string} peptide - Amino acid change passed to the fetch function.
 * @property {string} codon  - Coding sequence change passed to the fetch function.
 * @property {string} cancer - Cancer type keyword; only consumed by "cBioPortal".
 */

/**
 * Renders a single database result panel. On mount (and whenever props change),
 * it calls the appropriate fetch function from {@link databases}, then displays
 * the result. For the "cBioPortal" dataset the result is a React element (a
 * Chart.js bar chart); for all others it is an HTML string rendered via
 * `dangerouslySetInnerHTML`. Each panel includes a react-csv download link.
 *
 * @param {DataContainerProps} props
 * @returns {React.ReactElement}
 */
function DataContainer({ dataset, gene, peptide, codon, cancer }) {
  const [output, setOutput] = useState("");
  const [header, setHeader] = useState("");
  const [data, setData] = useState();
  const navigate = useNavigate();
  const func = databases.get(dataset);
  const fetchData = useCallback(async () => {
    const newOutput = await func(gene, peptide, codon, cancer);
    setOutput(newOutput);
  });
  useEffect(() => {
    fetchData().catch(console.error);
  }, [fetchData]);
  if (dataset === "cBioPortal") {
    return (
      <div className="data-container">
        <div className="dataset-name">{dataset}</div>
        <div className="csv">
          <CSVLink
            data={csvMap.get(dataset)}
            headers={headersMap.get(dataset)}
            filename={`Varstack2-${dataset}${gene}${peptide}${codon}.csv`}
          >
            Download as CSV
          </CSVLink>
        </div>
        {output}
      </div>
    );
  }
  return (
    <div className="data-container">
      <div className="dataset-name">{dataset}</div>
      <div className="csv">
        <CSVLink
          data={csvMap.get(dataset)}
          headers={headersMap.get(dataset)}
          filename={`Varstack2-${dataset}${gene}${peptide}${codon}.csv`}
        >
          Download as CSV
        </CSVLink>
      </div>
      <div
        className="data-table"
        dangerouslySetInnerHTML={{ __html: output }}
      ></div>
    </div>
  );
}

/**
 * @typedef {Object} OneInputProps
 * @property {string}   value       - Current value of the text field.
 * @property {Function} setValue    - State setter called on every keystroke.
 * @property {string}   placeholder - Placeholder text shown when the field is empty.
 * @property {string}   label       - Accessible label rendered above the input.
 */

/**
 * A labelled, controlled text input.
 *
 * @param {OneInputProps} props
 * @returns {React.ReactElement}
 */
function OneInput({ value, setValue, placeholder, label }) {
  return (
    <div className="input_fields_main">
      <Form>
        <Form.Group>
          <Form.Label>{label}</Form.Label>
          <Form.Control
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(ev) => setValue(ev.target.value)}
          />
        </Form.Group>
      </Form>
    </div>
  );
}

/**
 * A `<select>` element populated with all cancer types from `cancer-types.json`,
 * sorted alphabetically by display name. The selected value is a `cancerTypeId`
 * string used as the search keyword for the cBioPortal studies endpoint.
 *
 * @param {CancerSelectProps} props
 * @returns {React.ReactElement}
 */
function CancerSelect({ value, setValue }) {
  return (
    <select
      className="input-box-cancer"
      value={value}
      onChange={(ev) => setValue(ev.target.value)}
    >
      <option value="choose"> -- Select Cancer --</option> {ListCancers()}
    </select>
  );
}

/**
 * Generates a sorted list of `<option>` elements from the `cancer_types` JSON
 * array. Each option's value is the cancer's `cancerTypeId` and its label
 * combines the human-readable name with the ID in parentheses.
 *
 * @returns {React.ReactElement[]} Array of `<option>` elements sorted A–Z by name.
 */
function ListCancers() {
  const sortedCancers = [...cancer_types].sort((a, b) =>
    a.name.localeCompare(b.name),
  );

  return sortedCancers.map((cancer, index) => {
    return (
      <option key={"can-${index}"} value={cancer.cancerTypeId}>
        {`${cancer.name} (${cancer.cancerTypeId})`}
      </option>
    );
  });
}

/**
 * Converts a plain array of strings into an array of `<option>` React elements.
 
 *
 * @param {string[]} element  - Array of option values/labels.
 * @param {string}  [tempname="op"] - Prefix for generated React keys.
 * @returns {React.ReactElement[]}
 */
function ListElement(element, tempname = "op") {
  return element.map((ele, index) => {
    if (ele === "Select Here") {
      return (
        <option value={ele} key={`${tempname}${index}`}>
          {ele}
        </option>
      );
    }
    return <option value={ele}>{ele}</option>;
  });
}

/**
 * @typedef {Object} SmartSearchCodonProps
 * @property {string}   gene       - Hugo gene symbol used to populate the dropdown.
 * @property {string}   peptide    - Current amino acid filter; refines COSMIC query.
 * @property {string}   codon      - Current codon filter; refines COSMIC query.
 * @property {Function} putElement - Callback invoked with the newly selected codon string.
 */

/**
 * A smart dropdown that fetches available coding sequence changes for the current
 * gene (and optional peptide/codon filters) from the COSMIC API, then renders
 * them as selectable options. Excludes entries containing "?" (ambiguous changes).
 * Results are sorted alphabetically.
 *
 * @param {SmartSearchCodonProps} props
 * @returns {React.ReactElement}
 */
function SmartSearchCodon({ gene, peptide, codon, putElement }) {
  const [element, setElement] = useState("");
  const [codonList, setCodonList] = useState([]);
  const onChange = (ev) => {
    setElement(ev.target.value);
    putElement(ev.target.value);
  };
  /**
   * Fetches coding sequence changes from COSMIC and updates the local codon list.
   * Re-runs whenever `gene`, `peptide`, or `codon` changes (via `useEffect`).
   */
  const fetchData = useCallback(async () => {
    let link = "";
    if (peptide === "" && codon === "") {
      link = `https://clinicaltables.nlm.nih.gov/api/cosmic/v4/search?terms=${gene}&maxList=&grchv=38&df=AccessionNumber,GeneCDS_Length,GeneName,HGNC_ID,MutationAA,MutationCDS,MutationDescription,MutationGenomePosition,MutationStrand,MutationID,LegacyMutationID,GenomicMutationID,Name,PrimaryHistology,PrimarySite,PubmedPMID,Site,GRChVer`;
    } else if (peptide === "") {
      link = `https://clinicaltables.nlm.nih.gov/api/cosmic/v4/search?terms=${gene},${codon}&maxList=&grchv=38&df=AccessionNumber,GeneCDS_Length,GeneName,HGNC_ID,MutationAA,MutationCDS,MutationDescription,MutationGenomePosition,MutationStrand,MutationID,LegacyMutationID,GenomicMutationID,Name,PrimaryHistology,PrimarySite,PubmedPMID,Site,GRChVer`;
    } else if (codon === "") {
      link = `https://clinicaltables.nlm.nih.gov/api/cosmic/v4/search?terms=${gene},"${peptide}"&maxList=&grchv=38&df=AccessionNumber,GeneCDS_Length,GeneName,HGNC_ID,MutationAA,MutationCDS,MutationDescription,MutationGenomePosition,MutationStrand,MutationID,LegacyMutationID,GenomicMutationID,Name,PrimaryHistology,PrimarySite,PubmedPMID,Site,GRChVer`;
    } else {
      link = `https://clinicaltables.nlm.nih.gov/api/cosmic/v4/search?terms=${gene},"${peptide}",${codon}&maxList=&grchv=38&df=AccessionNumber,GeneCDS_Length,GeneName,HGNC_ID,MutationAA,MutationCDS,MutationDescription,MutationGenomePosition,MutationStrand,MutationID,LegacyMutationID,GenomicMutationID,Name,PrimaryHistology,PrimarySite,PubmedPMID,Site,GRChVer`;
    }

    codons = await fetch(link)
      .then((response) => response.json())
      .then((responseObject) => {
        let temp = responseObject[3];
        let codonArray = [];

        for (let j = 0; j < temp.length; j++) {
          if (!temp[j][5].includes("?")) {
            if (!codonArray.includes(temp[j][5])) {
              codonArray.push(temp[j][5]);
            }
          }
        }
        codonArray.sort();
        codonArray.unshift("Select Here");
        return codonArray;
      });
    setCodonList(codons);
  });
  useEffect(() => {
    fetchData().catch(console.error);
  }, [fetchData]);
  return (
    <div className="smart-search">
      <div className="input-box">
        <div className="peptide_change_selector">
          Choose Coding Sequence Change in Gene <br />
          <select
            className="input-box-peptide"
            value={element}
            onChange={onChange}
          >
            <option value="choose" disabled>
              {" "}
              -- Select Coding Sequence Change --
            </option>{" "}
            {ListElement(codonList, "a")}
          </select>
        </div>
      </div>
    </div>
  );
}

/**
 * @typedef {Object} SmartSearchProps
 * @property {Function} putElement - Callback invoked with the newly selected value.
 * @property {string}   eleName    - Human-readable name for the change type
 *   (e.g. "Amino Acid"), shown in the label and placeholder.
 * @property {string[]} eleList    - Array of options to display
 */

/**
 * A generic smart dropdown that renders a pre-populated list of variant change
 * options (e.g. amino acid changes).
 *
 * @param {SmartSearchProps} props
 * @returns {React.ReactElement}
 */
function SmartSearch({ putElement, eleName, eleList }) {
  const [element, setElement] = useState("");
  const onChange = (ev) => {
    putElement(ev.target.value);
    setElement(ev.target.value);
  };
  return (
    <div className="smart-search">
      <div className="input-box">
        <div className="peptide_change_selector">
          Choose {eleName} Change in Gene <br />
          <select
            className="input-box-peptide"
            value={element}
            onChange={onChange}
          >
            <option value="choose" disabled>
              {" "}
              -- Select {eleName} Change --
            </option>{" "}
            {ListElement(eleList, "b")}
          </select>
        </div>
      </div>
    </div>
  );
}

/**
 * @typedef {Object} GeneInputsProps
 * @property {string}   curPeptide     - Current peptide value in the parent's state.
 * @property {Function} putGene        - Lifts the confirmed gene value to the parent.
 * @property {Function} putPeptide     - Lifts the confirmed peptide value to the parent.
 * @property {Function} putCodon       - Lifts the confirmed codon value to the parent.
 * @property {Function} putCancer      - Lifts the confirmed cancer type to the parent.
 * @property {Function} putPeptideList - Lifts the fetched amino acid change list to the parent.
 * @property {Function} putCodonList   - Lifts the fetched coding sequence change list to the parent.
 */

/**
 * The main input panel containing four controlled fields (gene, peptide, codon,
 * and cancer type) and a SUBMIT button. On submit it:
 * 1. Lifts all field values to the parent via the `put*` callbacks.
 * 2. Fetches available amino acid and coding sequence changes from COSMIC for the
 *    current query and lifts them to the parent for use by the smart dropdowns.
 * 3. Updates the URL search params to make the query bookmarkable/shareable.
 *
 * Also reads initial field values from URL search params on mount so that shared
 * or bookmarked URLs pre-populate the form.
 *
 * @param {GeneInputsProps} props
 * @returns {React.ReactElement}
 */
function GeneInputs({
  curPeptide,
  putGene,
  putPeptide,
  putCodon,
  putCancer,
  putPeptideList,
  putCodonList,
}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [gene, setGene] = useState("");
  const [peptide, setPeptide] = useState("");
  const [codon, setCodon] = useState("");
  const [cancer, setCancer] = useState("");
  const [peptideList, setPeptideList] = useState("");
  const [codonList, setCodonList] = useState("");
  useEffect(() => {
    if (searchParams && searchParams.get("gene") !== null) {
      setGene(searchParams.get("gene"));
    }
    if (searchParams && searchParams.get("peptide")) {
      setPeptide(searchParams.get("peptide"));
    }
    if (searchParams && searchParams.get("codon")) {
      setCodon(searchParams.get("codon"));
    }
    if (searchParams && searchParams.get("cancer")) {
      setCancer(searchParams.get("cancer"));
    }
  }, [searchParams]);
  return (
    <Row className="inputs">
      <div className="input-boxes">
        {" "}
        <OneInput
          label="Enter Gene Name"
          value={gene}
          setValue={setGene}
          placeholder="KRAS"
        />{" "}
        <OneInput
          label="Enter Amino Acid Change"
          value={peptide}
          setValue={setPeptide}
          placeholder="p.G12V"
        />{" "}
        <OneInput
          label="Enter Coding Sequence Change"
          value={codon}
          setValue={setCodon}
          placeholder="c.35G>T"
        />
        <div className="cancer_select">
          {" "}
          <CancerSelect
            label="Enter Cancer Type (for cBioPortal)"
            value={cancer}
            setValue={setCancer}
          />
        </div>
      </div>
      <div className="btn_container">
        <button
          className="submit-button"
          // role="button"
          onClick={async () => {
            putGene(gene);
            putPeptide(peptide);
            putCodon(codon);
            putCancer(cancer);
            let link = "";
            if (peptide === "" && codon === "") {
              link = `https://clinicaltables.nlm.nih.gov/api/cosmic/v4/search?terms=${gene}&maxList=&grchv=38&df=AccessionNumber,GeneCDS_Length,GeneName,HGNC_ID,MutationAA,MutationCDS,MutationDescription,MutationGenomePosition,MutationStrand,MutationID,LegacyMutationID,GenomicMutationID,Name,PrimaryHistology,PrimarySite,PubmedPMID,Site,GRChVer`;
            } else if (peptide === "") {
              link = `https://clinicaltables.nlm.nih.gov/api/cosmic/v4/search?terms=${gene},${codon}&maxList=&grchv=38&df=AccessionNumber,GeneCDS_Length,GeneName,HGNC_ID,MutationAA,MutationCDS,MutationDescription,MutationGenomePosition,MutationStrand,MutationID,LegacyMutationID,GenomicMutationID,Name,PrimaryHistology,PrimarySite,PubmedPMID,Site,GRChVer`;
            } else if (codon === "") {
              link = `https://clinicaltables.nlm.nih.gov/api/cosmic/v4/search?terms=${gene},"${peptide}"&maxList=&grchv=38&df=AccessionNumber,GeneCDS_Length,GeneName,HGNC_ID,MutationAA,MutationCDS,MutationDescription,MutationGenomePosition,MutationStrand,MutationID,LegacyMutationID,GenomicMutationID,Name,PrimaryHistology,PrimarySite,PubmedPMID,Site,GRChVer`;
            } else {
              link = `https://clinicaltables.nlm.nih.gov/api/cosmic/v4/search?terms=${gene},"${peptide}",${codon}&maxList=&grchv=38&df=AccessionNumber,GeneCDS_Length,GeneName,HGNC_ID,MutationAA,MutationCDS,MutationDescription,MutationGenomePosition,MutationStrand,MutationID,LegacyMutationID,GenomicMutationID,Name,PrimaryHistology,PrimarySite,PubmedPMID,Site,GRChVer`;
            }
            peptides = await fetch(link)
              .then((response) => response.json())
              .then((responseObject) => {
                let temp = responseObject[3];
                let peptideArray = [];

                for (let j = 0; j < temp.length; j++) {
                  if (!temp[j][4].includes("?")) {
                    if (!peptideArray.includes(temp[j][4])) {
                      peptideArray.push(temp[j][4]);
                    }
                  }
                }
                peptideArray.sort();
                peptideArray.unshift("Select Here");

                return peptideArray;
              });
            setPeptideList(peptides);
            putPeptideList(peptides);
            codons = await fetch(link)
              .then((response) => response.json())
              .then((responseObject) => {
                let temp = responseObject[3];
                let codonArray = [];

                for (let j = 0; j < temp.length; j++) {
                  if (!temp[j][5].includes("?")) {
                    if (!codonArray.includes(temp[j][5])) {
                      codonArray.push(temp[j][5]);
                    }
                  }
                }
                codonArray.sort();
                return codonArray;
              });
            setCodonList(codons);
            putCodonList(codons);
            navigate(
              `/?gene=${gene}&peptide=${peptide}&codon=${codon}&cancer=${cancer}`,
            );
          }}
        >
          SUBMIT
        </button>
      </div>
    </Row>
  );
}

/**
 * Exports data from all registered databases to a single multi-sheet XLSX file.
 * Each database gets its own worksheet named after the database. The file is
 * written to disk immediately via SheetJS's `writeFile`.
 *
 * @param {string} gene    - Hugo gene symbol; included in the filename.
 * @param {string} peptide - Amino acid change; included in the filename.
 * @param {string} codon   - Coding sequence change; included in the filename.
 * @returns {void}
 */
function handleOnExport(gene, peptide, codon) {
  let wb = XLSX.utils.book_new();
  let ws_1 = XLSX.utils.json_to_sheet(csvMap.get("COSMIC"));
  XLSX.utils.book_append_sheet(wb, ws_1, "COSMIC");
  let ws_2 = XLSX.utils.json_to_sheet(csvMap.get("ClinVar"));
  XLSX.utils.book_append_sheet(wb, ws_2, "ClinVar");
  let ws_3 = XLSX.utils.json_to_sheet(csvMap.get("cBioPortal"));
  XLSX.utils.book_append_sheet(wb, ws_3, "cBioPortal");
  let ws_4 = XLSX.utils.json_to_sheet(csvMap.get("UCSC Genome Browser"));
  XLSX.utils.book_append_sheet(wb, ws_4, "UCSC Genome Browser");
  let ws_5 = XLSX.utils.json_to_sheet(csvMap.get("ClinicalTrials.gov"));
  XLSX.utils.book_append_sheet(wb, ws_5, "ClinicalTrials.gov");

  XLSX.writeFile(wb, `Varstack2${gene},${peptide},${codon}.xlsx`);
}

/**
 * Root page component for VarStack2.
 *
 * Manages the shared query state (gene, peptide, codon, cancer, and the two
 * smart-search lists). Renders:
 * - The app logo
 * - {@link GeneInputs} — the main search form
 * - {@link SmartSearch} — amino acid change dropdown
 * - {@link SmartSearchCodon} — coding sequence change dropdown
 * - An "Export All Databases" button that triggers {@link handleOnExport}
 * - A {@link DataContainer} for every entry in {@link databases}
 *
 * @returns {React.ReactElement}
 */
export default function Window() {
  const [gene, setGene] = useState("");
  const [peptide, setPeptide] = useState("");
  const [codon, setCodon] = useState("");
  const [cancer, setCancer] = useState("");
  const [peptideList, setPeptideList] = useState("");
  const [codonList, setCodonList] = useState("");

  let rows = [];

  for (let [key, value] of databases) {
    rows.push(
      <DataContainer
        dataset={key}
        gene={gene}
        peptide={peptide}
        codon={codon}
        cancer={cancer}
      />,
    );
  }
  return (
    <div className="window">
      <img className="varstack-logo" src={logo} alt="" />

      <div className="parent">
        <div className="child data">
          <div className="input">
            <GeneInputs
              curPeptide={peptide}
              putGene={(newGene) => {
                setGene(newGene);
              }}
              putPeptide={(newPeptide) => {
                setPeptide(newPeptide);
              }}
              putCodon={(newCodon) => {
                setCodon(newCodon);
              }}
              putCancer={(newCancer) => {
                setCancer(newCancer);
              }}
              putPeptideList={(newPeptideList) => {
                setPeptideList(newPeptideList);
              }}
              putCodonList={(newCodonList) => {
                setCodonList(newCodonList);
              }}
            />
          </div>
          <div>
            <SmartSearch
              putElement={(newPeptide) => {
                setPeptide(newPeptide);
              }}
              eleName={"Amino Acid"}
              eleList={peptides}
            />
            <SmartSearchCodon
              putElement={(newCodon) => {
                setCodon(newCodon);
              }}
              gene={gene}
              codon={codon}
              peptide={peptide}
            />
          </div>
        </div>
      </div>
      <div style={{ marginTop: "0.5rem" }}>
        <Button
          className="submit-button"
          onClick={() => handleOnExport(gene, peptide, codon)}
        >
          Export All Databases
        </Button>
      </div>
      <br></br>
      <p>
        The COSMIC database has been removed due to licensing requirements. We
        apologize for the inconvenience.
      </p>
      <div className="data-container-list">{rows}</div>
    </div>
  );
}
